import api from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  first_name?: string;
  last_name?: string;
  referral_code?: string;
}

export interface Client {
  id: string;
  name: string;
  // Add other client properties as needed
}

export interface User {
  id: string;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  avatar_url?: string;
  is_active: boolean;
  created_at: string;
  is_admin: boolean;
  is_beta_tester: boolean;
  credits: number;
  client?: Client; // Added client property
  tier?: {
    id: string;
    name: string;
    display_name: string;
    price_monthly?: number;
    price_yearly?: number;
  };
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    console.log('AuthService: Calling API for login...');
    try {
      const response = await api.post('/auth/login', credentials);
      console.log('AuthService: API response received:', response);

      // Check if login was successful based on the presence of tokens
      if (!response.data.access_token || !response.data.refresh_token) {
        console.warn('AuthService: Login response missing tokens, throwing error.', response.data);
        // Assuming backend returns 200 OK even on failed login, but without tokens
        // Or, if backend sends a specific error message in data, check for that too
        throw new Error(response.data.detail || 'Invalid credentials or account not found.');
      }

      const { access_token, refresh_token } = response.data;

      // Store tokens in localStorage
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      console.log('AuthService: Tokens stored, returning response.');
      return response.data;
    } catch (error) {
      console.error('AuthService: Error during API call:', error);
      throw error; // Re-throw the error
    }
  }

  async register(userData: RegisterData): Promise<User> {
    const response = await api.post('/auth/register', userData);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh', {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token: newRefreshToken } = response.data;
    localStorage.setItem('access_token', access_token);

    // Update refresh token if a new one is provided (token rotation)
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }

    return access_token;
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/users/profile');
    return response.data;
  }

  // New function to update user credits
  async updateUserCredits(userId: string, operation: 'ADD' | 'SUBTRACT', amount: number): Promise<User> {
    const response = await api.post(`/users/credits`, { user_id: userId, operation, amount });
    return response.data;
  }

  async getAllUsers(): Promise<User[]> {
    const response = await api.get<User[]>('/users/'); // Now this endpoint exists and returns all users
    return response.data;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Beta tester status is now managed in the backend via user.is_beta_tester field
  // These methods are kept for backward compatibility but deprecated
  isBetaTester(userEmail?: string): boolean {
    console.warn('authService.isBetaTester() is deprecated. Use user.is_beta_tester instead.');
    return false;
  }

  getBetaTesterEmails(): string[] {
    console.warn('authService.getBetaTesterEmails() is deprecated. Beta testers are now managed via admin panel.');
    return [];
  }

  async devVerifyUser(email: string): Promise<void> {
    await api.post(`/auth/dev-verify-user?email=${encodeURIComponent(email)}`);
  }
}

export default new AuthService();